import { $, $$, body } from "@utils/dom";
import { on, off } from "@utils/listener";
import { mobile } from "@utils/mobile";
import Throttle from "@utils/throttle";
import Breakpoint from "@utils/breakpoint";
import ResizeOrientation from "@utils/resize";

const BREAKPOINTS = ["(max-width: 1200px)"];

export const SELECTOR = "[data-site-super-menu]";
export const NAV_SELECTOR = ".site-super-menu__nav";
export const MENU_DELAY = 450;
export const TRANSITION_DELAY = 450;

class SiteSuperMenu {
  constructor(init = false) {
    this.initialized = false;
    this.opened = false;
    this.el = null;

    this.currentId = null;
    this.currentDelay = MENU_DELAY;
    this.transitionDelay = 0;

    this._onClick = this._onClick.bind(this);
    this._open = this._open.bind(this);
    this._selectCurrentNav = this._selectCurrentNav.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._close = this._close.bind(this);
    this._onCloseCompleted = this._onCloseCompleted.bind(this);
    this._toggle = this._toggle.bind(this);
    this._setSiteHeaderWidth = this._setSiteHeaderWidth.bind(this);

    init ? this.init() : null;
  }

  get name() {
    return `SiteSuperMenu`;
  }

  init() {
    this.initialized = true;
    this.el = $(SELECTOR);

    this.backdrop = $('.site-super-menu__backdrop', this.el);
    this.container = $('.site-super-menu__container', this.el);
    this.triggers = [...$$(`[aria-controls="${this.el.id}"]`)];
    this.navs = [...$$(NAV_SELECTOR, this.el)];

    this.siteHeader = $('.site-header');

    this.navThrottler = new Throttle({cb: this._toggle, delay: 250, onlyAtEnd: false});
    this._bp = new Breakpoint(BREAKPOINTS, this._setSiteHeaderWidth);

    this._bp.run();

    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    if (this._bp) this._bp.dispose();
    this._bp = null;

    this.el = null;
    this.initialized = false;
    this.opened = false;

    this.backdrop = null;
    this.container = null;
    this.triggers = null;
    this.navs = null;

    this.siteHeader = null;

    if(this.navThrottler) this.navThrottler.dispose();
    this.navThrottler = null;

    this.currentId = null;
    this.currentDelay = null;
  }

  _bindEvents() {
    this._bp?.on();
    ResizeOrientation.add(this._setSiteHeaderWidth);

    if( mobile ) {
      if( this.triggers ) on(this.triggers, 'click', this._onClick);
      if( this.backdrop ) on(this.backdrop, 'click', this._onLeave);
    } else {
      if( this.triggers ) on(this.triggers, 'click', this._onClick);
      if( this.backdrop ) on(this.backdrop, 'mouseenter', this._onLeave);
    }
    
    this.emitter.on(`SiteScroll.direction`, this._onLeave);
    this.emitter.on('SiteContactPanel.open', this._onLeave);
  }
  _unbindEvents() {
    this._bp?.off();
    ResizeOrientation.remove(this._setSiteHeaderWidth);

    if( mobile ) {
      if( this.triggers ) off(this.triggers, 'click', this._onClick);
      if( this.backdrop ) off(this.backdrop, 'click', this._onLeave);
    } else {
      if( this.triggers ) off(this.triggers, 'click', this._onClick);
      if( this.backdrop ) off(this.backdrop, 'mouseenter', this._onLeave);
    }

    off(this.backdrop, 'transitionend', this._onCloseCompleted);
    
    this.emitter.off(`SiteScroll.direction`, this._onLeave);
    this.emitter.off('SiteContactPanel.open', this._onLeave);
  }

  _onClick(e) {
    if( e ) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    
    const navID = e.currentTarget.getAttribute('data-nav-id');

    this.currentId = this.currentId !== navID ? navID : null;
    this._toggle();

    return false;
  }

  _selectCurrentNav(){
    this.navs.forEach((_el) => {
      if (_el.getAttribute('data-id') == this.currentId) {
        _el.setAttribute('aria-hidden', false);
      } else {
        _el.setAttribute('aria-hidden', true);
      }
    });

    this.triggers.forEach((_el) => {
      if (_el.getAttribute('data-nav-id') == this.currentId) {
        _el.setAttribute('aria-expanded', true);
      } else {
        _el.setAttribute('aria-expanded', false);
      }
    });
  }

  _onLeave(){
    this.currentId = null;
    this.navThrottler.init();
  }
  _onCloseCompleted() {
    off(this.backdrop, 'transitionend', this._onCloseCompleted);
    this.el.setAttribute('aria-hidden', true);
  }

  _open(){
    this.opened = true;

    off(this.backdrop, 'transitionend', this._onCloseCompleted);

    this.el.setAttribute('aria-hidden', false);
    body.classList.add('--js-site-super-menu-opened');

    this.emitter.emit("SiteSuperMenu.open");

    setTimeout(()=> {
      this.currentDelay = 0;
      this.el.style.setProperty('--menu-delay',`${this.currentDelay}ms`);

      this.transitionDelay = TRANSITION_DELAY;
      this.el.style.setProperty('--transition-delay',`${this.transitionDelay}ms`);

    }, 50);
  }
  _close(){
    this.opened = false;

    off(this.backdrop, 'transitionend', this._onCloseCompleted);
    on(this.backdrop, 'transitionend', this._onCloseCompleted);

    body.classList.remove('--js-site-super-menu-opened');
    this.emitter.emit("SiteSuperMenu.close");

    // add delay after element open
    this.currentDelay = MENU_DELAY;
    this.el.style.setProperty('--menu-delay',`${this.currentDelay}ms`);

    this.transitionDelay = 0;
    this.el.style.setProperty('--transition-delay',`${this.transitionDelay}ms`);
  }
  _toggle(){
    this._selectCurrentNav();
    if(this.currentId){
      this._open();
    } else {
      this._close();
    }
  }

  _setSiteHeaderWidth(){
    const menuHeader = $('.site-header__menu', this.siteHeader);
    this.el.style.setProperty('--site-header-menu-width',`${menuHeader.clientWidth}px`);
  }
}

export default SiteSuperMenu;
